/**
 * Which workspace an exception came from (#3568). Every `$exception` sent to
 * PostHog (Convex server capture and the browser client) carries the
 * workspace slug and display name, so error alerts can say where it
 * happened. Both are workspace identity, not person data; nothing else about
 * the workspace is attached. Unknown workspaces read as `unknown`; browser
 * errors after sign-out read as `signed-out`.
 *
 * The error sanitizer redacts `*_slug` and `organization_name` keys, so the
 * capture paths add these after sanitizing.
 */
export const WORKSPACE_SLUG_PROPERTY = 'workspace_slug';
export const WORKSPACE_NAME_PROPERTY = 'workspace_name';
/** Browser only: the server capture already carries `organizationId`. */
export const WORKSPACE_ID_PROPERTY = 'workspace_id';
export const UNKNOWN_WORKSPACE = 'unknown';
export const SIGNED_OUT_WORKSPACE = 'signed-out';

const MAX_LABEL_LENGTH = 120;

export interface WorkspaceLabel {
  slug: string;
  name: string;
  /** Organization id, when the reporter has it (the browser boundary does). */
  id?: string;
}

export type WorkspaceErrorProperties = {
  [WORKSPACE_SLUG_PROPERTY]: string;
  [WORKSPACE_NAME_PROPERTY]: string;
};

function cleanLabel(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  if (!trimmed || trimmed === UNKNOWN_WORKSPACE) return undefined;
  return trimmed.slice(0, MAX_LABEL_LENGTH);
}

export function workspaceErrorProperties(
  label: Partial<WorkspaceLabel> | null | undefined
): WorkspaceErrorProperties {
  const slug = cleanLabel(label?.slug);
  return {
    [WORKSPACE_SLUG_PROPERTY]: slug ?? UNKNOWN_WORKSPACE,
    // A name without its slug can't be told apart from another workspace.
    [WORKSPACE_NAME_PROPERTY]:
      (slug && cleanLabel(label?.name)) ?? UNKNOWN_WORKSPACE,
  };
}

let lastBrowserWorkspace: WorkspaceLabel | null = null;
let browserSignedOut = false;

/**
 * Browser half: the admitted workspace boundary reports the workspace it
 * renders, and the label only applies while the page is under `/<slug>/`.
 */
export function setActiveErrorWorkspace(label: WorkspaceLabel): void {
  lastBrowserWorkspace = label;
  browserSignedOut = false;
}

/**
 * Sign-out: forgets the workspace and labels later browser errors
 * `signed-out`, so they aren't mistaken for errors from an unknown workspace.
 */
export function markErrorWorkspaceSignedOut(): void {
  lastBrowserWorkspace = null;
  browserSignedOut = true;
}

/**
 * Forgets the reported workspace: all of it on sign-out or lost access, or
 * only `slug` when that workspace's boundary goes away (so a newer
 * workspace's label stays).
 */
export function clearActiveErrorWorkspace(slug?: string): void {
  if (slug === undefined || lastBrowserWorkspace?.slug === slug) {
    lastBrowserWorkspace = null;
  }
}

/** The last reported workspace, if `pathname` is one of its routes. */
export function browserWorkspaceForPath(
  pathname: string | null | undefined
): WorkspaceLabel | null {
  const workspace = lastBrowserWorkspace;
  if (!workspace || typeof pathname !== 'string') return null;
  const firstSegment = pathname.split('/').find(Boolean);
  return firstSegment === workspace.slug ? workspace : null;
}

type BrowserCaptureEvent = {
  event: string;
  properties: Record<string, unknown>;
};

function currentPathname(properties: Record<string, unknown>): string | null {
  if (typeof properties.$pathname === 'string') return properties.$pathname;
  return typeof window === 'undefined'
    ? null
    : (window.location?.pathname ?? null);
}

/** before_send step: stamps browser `$exception` events with the workspace. */
export function addWorkspaceToBrowserException<T extends BrowserCaptureEvent>(
  event: T | null
): T | null {
  if (!event || event.event !== '$exception') return event;
  const workspace = browserWorkspaceForPath(currentPathname(event.properties));
  return {
    ...event,
    properties: {
      ...event.properties,
      ...(!workspace && browserSignedOut
        ? {
            [WORKSPACE_SLUG_PROPERTY]: SIGNED_OUT_WORKSPACE,
            [WORKSPACE_NAME_PROPERTY]: SIGNED_OUT_WORKSPACE,
          }
        : workspaceErrorProperties(workspace)),
      ...(workspace?.id ? { [WORKSPACE_ID_PROPERTY]: workspace.id } : {}),
    },
  };
}
